import {
  eed_mario_icon_folder_excel_area,
  eed_mario_icon_folder_pdf_area,
  eed_mario_icon_folder_word_area,
  eed_mario_icon_folder_code_area,
  eed_web_icon_global_05_folder_txt_area,
  eed_web_icon_global_05_folder_video_area,
  eed_web_icon_global_05_folder_radio_area,
  eed_mario_icon_folder_ppt_area,
} from '@src/assets/icons';
import intl from '@/internationalzation/intl';
import { v4 } from 'uuid';
import { IFile } from './local-files-helper';
import { isKim, isMobile } from './user-agent';
import { getPreviewUrl } from './route';
import { Message } from '@docs/kd-d2c-components';
export const INPUT_ID = 'file-upload-input';
export class FileHelper {
  // 最大的文件数量
  static maxFileCount = 10;
  // 最大并发数
  static maxConcurrent = 10;
  // 最大文件大小
  static maxFileSize = 20 * 1024 * 1024;

  static types = {
    // 文档
    doc: {
      icon: eed_mario_icon_folder_word_area,
      accept: ['doc', 'docx'],
    },
    excel: {
      icon: eed_mario_icon_folder_excel_area,
      accept: ['xls', 'xlsx', 'csv'],
    },
    image: {
      icon: [''],
      accept: ['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp', 'svg', 'ico'],
    },
    pdf: {
      icon: eed_mario_icon_folder_pdf_area,
      accept: ['pdf'],
    },
    ppt: {
      icon: eed_mario_icon_folder_ppt_area,
      accept: ['ppt', 'pptx'],
    },
    txt: {
      icon: eed_web_icon_global_05_folder_txt_area,
      accept: ['txt'],
    },
    video: {
      icon: eed_web_icon_global_05_folder_video_area,
      accept: ['mov'],
    },
    audio: {
      icon: eed_web_icon_global_05_folder_radio_area,
      accept: ['mp3', 'm4a'],
    },
    code: {
      icon: eed_mario_icon_folder_code_area,
      accept: [
        'md',
        'json',
        'xml',
        'html',
        'css',
        'js',
        'cp',
        'c',
        'cpp',
        'java',
        'py',
        'php',
        'm',
        'xml',
      ],
    },
  };
  static notAllowedTypes = [
    'htm',
    'jsp',
    'jspx',
    'svg',
    'swf',
    'css',
    'mjs',
    'es',
    'ecma',
    'jscript',
    'live-script',
    'cjs',
    'vbs',
    'hta',
    'asp',
    'xhtml',
  ];
  // icon 列表
  static fileIconMap = (() => {
    const map: Record<string, string[]> = {};
    for (const key in FileHelper.types) {
      const type = FileHelper.types[key as keyof typeof FileHelper.types];
      if (type && type.accept) {
        type.accept.forEach((suffix: string) => {
          map[suffix] = type.icon;
        });
      }
    }
    return map;
  })();

  // 支持的文件后缀列表
  static accepts = Object.keys(FileHelper.fileIconMap);

  static getIcon(type: string) {
    return FileHelper.fileIconMap[type] || eed_mario_icon_folder_code_area;
  }

  static cloudDocTypes = [
    'cloud_doc',
    'sheet',
    'metaSheet',
    'powerPoint',
    'whiteBoard',
    'folder',
    'yfile',
  ];

  static isCloudDoc(type: string) {
    return FileHelper.cloudDocTypes.includes(type);
  }

  static groupFiles(files: any[]) {
    const group = [
      {
        key: 'all',
        label: intl.t('全部'),
        children: files,
      },
      {
        key: 'cloudDoc',
        label: intl.t('云文档'),
        children: files.filter((item) => FileHelper.isCloudDoc(item.fileType)),
      },
      {
        key: 'files',
        label: intl.t('文件'),
        children: files.filter((item) => FileHelper.isLocalFile(item.fileType)),
      },
      {
        key: 'images',
        label: intl.t('图片'),
        children: files.filter((item) => FileHelper.isImage(item.fileType)),
      },
      // {
      //   key: 'link',
      //   label: '链接',
      //   children: files.filter((item) => item.fileType === 'link'),
      // },
    ];

    return group;
  }

  /**
   * 将文件大小转换为最适合的单位表示
   * @param bytes 文件大小（字节）
   * @param decimals 小数位数，默认为2
   * @returns 格式化后的文件大小字符串
   */
  static getSize(bytes: number, decimals: number = 2) {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];

    // 计算合适的单位级别
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    // 转换到对应单位并保留指定小数位
    const size = parseFloat((bytes / Math.pow(k, i)).toFixed(decimals));
    return `${size} ${sizes[i]}`;
  }

  // 获取文件扩展名
  static getExtension(filename: string) {
    const suffixReg = /.([^.]+)$/;
    const suffixRegRes = suffixReg.exec(filename);
    return suffixRegRes ? suffixRegRes[1].toLowerCase() : '';
  }

  // 移除文件扩展名
  static removeExtension(filename: string) {
    return filename.replace(/.([^.]+)$/, '');
  }

  // 自动调起系统文件选择
  static triggerFileSelect = ({
    type,
    multiple = false,
    beforeUpload,
    onSelect,
    onUpload,
  }: {
    type: 'image' | 'file' | 'capture';
    multiple?: boolean;
    beforeUpload?: (file: File) => boolean;
    onSelect?: (file: File[]) => void;
    onUpload: (file: File) => Promise<void>;
  }) => {
    // 创建 input 元素
    const input = document.createElement('input');
    input.id = INPUT_ID;
    input.type = 'file';
    input.style.display = 'none'; // 防止闪烁
    input.multiple = multiple;

    // 设置文件类型
    switch (type) {
      case 'image':
        input.accept = 'image/kim';
        break;
      case 'capture':
        input.accept = 'image/kim';
        input.capture = 'true';
        break;
      case 'file':
      default:
        input.accept = '*/*';
        break;
    }

    // 添加到 body
    document.body.appendChild(input);
    input.onchange = async (e: Event) => {
      const target = e.target as HTMLInputElement;
      let files = target.files ? Array.from(target.files) : [];
      files.forEach((file) => {
        file.uid = file.uid || v4();
      });
      if (files.length === 0) {
        cleanup();
        return;
      }

      // 过滤文件：beforeUpload
      if (beforeUpload) {
        const results = await Promise.all(
          files.map(async (file) => {
            const result = await beforeUpload(file);
            return result ? file : null;
          }),
        );
        files = results.filter((f): f is File => !!f);
      }

      // 回调选中文件
      if (onSelect) {
        onSelect(files);
      }

      // 自动上传
      if (files.length > 0) {
        await Promise.all(
          files.map(async (file) => {
            await onUpload(file);
          }),
        );
      }
      // 清理 DOM
      cleanup();
    };
    const cleanup = () => {
      input.remove(); // 移除 input
    };
    input.click();
  };
  static isImage(type: string) {
    return this.types.image.accept.includes(type);
  }

  static isCode(type: string) {
    return this.types.code.accept.includes(type);
  }

  static isLocalFile(type: string) {
    return [
      ...FileHelper.types.code.accept,
      ...FileHelper.types.excel.accept,
      ...FileHelper.types.ppt.accept,
      ...FileHelper.types.pdf.accept,
      ...FileHelper.types.doc.accept,
      ...FileHelper.types.txt.accept,
    ].includes(type);
  }

  static getDownloadUrl(objectKey: string) {
    if (!objectKey) return '';
    return `${location.origin}/api/file/download/${objectKey}`;
  }

  static base64ToFile(
    base64: string,
    name: string,
    type?: string,
    lastModified?: number,
  ) {
    // 将Base64字符串转换为Blob对象
    const byteString = atob(base64.split(',')[1]);
    const mimeString = base64.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([ab], { type: mimeString || type });
    const file = new File([blob], name, {
      type: mimeString || type,
      lastModified: lastModified,
    });
    file.uid = file.uid || v4();

    // 将Blob对象转换为File对象
    return file;
  }

  static getImagePreviewUrl(file: IFile) {
    if (file?.originalFile) {
      return URL.createObjectURL(file.originalFile);
    } else if (file instanceof File) {
      return URL.createObjectURL(file);
    }
    return '';
  }

  // static fileToBase64(file: File) {
  //   return new Promise((resolve, reject) => {
  //     const reader = new FileReader();
  //     reader.readAsDataURL(file);
  //     reader.onload = () => resolve(reader.result);
  //     reader.onerror = (error) => reject(error);
  //   });
  // }

  static async previewFileInKim(data: {
    type: string;
    url: string;
    downloadUrl: string;
    size: number;
    name: string;
    blobKey: string;
  }) {
    if (!isMobile || !isKim) return;
    const { kimBridge } = await import('@src/utils/kim-bridge');
    if (FileHelper.isCode(data.type)) {
      const url = getPreviewUrl(
        {
          blobKey: data.blobKey || '',
          fileExtension: data.type,
          fileName: data.name || '',
          fileSize: data.size?.toString() || '',
        },
        isKim,
      );
      kimBridge.openWebview(url);
      return;
    }

    if (FileHelper.isImage(data.type)) {
      const url = data.url || data.downloadUrl;
      if (!url) {
        Message.error({
          duration: 2000,
          message: intl.t('图片链接不存在'),
        });
        return;
      }
      return kimBridge.showInKim({
        thumb: url,
        thumbUrl: url,
        url: url,
      });
    }
    if (FileHelper.isCloudDoc(data.type) && data.url) {
      return kimBridge.openWebview(data.url);
    }
    return kimBridge.openDocumentFile({
      fileIdentity: data.blobKey,
      fileName: data.name,
      fileUrl: data.downloadUrl,
      fileSizeBytes: data.size,
    });
  }
}
